import { z } from 'zod';

import { listingCarbonClaimFieldsSchema } from './carbon.js';
import {
  deriveReportingEligibility,
  deriveUsePhaseAccuracyTier,
  PUE_SOURCE_VALUES,
  POWER_BASIS_VALUES,
  USE_PHASE_ACCURACY_TIER_VALUES,
  USE_PHASE_REPORTING_ELIGIBILITY_VALUES,
  usePhaseInputProvenanceSchema,
  type UsePhaseAccuracyTier,
  type UsePhaseInputProvenance,
  type UsePhasePowerBasis,
  type UsePhasePueSource,
  type UsePhaseReportingEligibility,
} from '../use-phase/accuracy-tier.js';
import {
  isUsePhaseMeasuredOnlyAssetClass,
  isUsePhaseSupportedAssetClass,
} from '../use-phase/asset-class-support.js';
import {
  deriveUsePhaseScreeningBasis,
  usePhaseScreeningBasisSchema,
} from '../use-phase/screening-basis.js';
import { applyPue, type BoundsBasis } from '../use-phase/use-phase-math.js';

/**
 * Use-phase (operational) carbon DTOs (SIL-342, T4 of
 * resources/plans/use-phase-carbon.md). Mirrors the conventions of
 * `dto/carbon.ts` (the embodied-carbon sibling) but at asset grain instead
 * of SKU-catalog grain.
 *
 * Backs (future T5/T6 work, not this ticket):
 *   ops + customer asset-detail use-phase block
 *   the appended ESG CSV columns (plan §"ESG CSV export")
 *   financial-scenario use-phase projections
 *
 * Source of truth is `db/schema/707_asset_use_phase_snapshot.sql`
 * (append-only; "latest" resolved by `v_asset_use_phase_latest`) plus the
 * SIL-341 derivation functions in `use-phase/accuracy-tier.ts`. There is
 * DELIBERATELY no stored `accuracy_tier` or `reporting_eligibility` column
 * — both are derived here, at read time, from a snapshot row's
 * `input_provenance`, every time. Never read/trust a stored tier.
 *
 * ---------------------------------------------------------------------------
 * BOUNDS FIELD — READ THIS BEFORE TOUCHING A LABEL OR TOOLTIP
 * ---------------------------------------------------------------------------
 * `gwpUseAnnualMinKgco2eq` / `gwpUseAnnualMaxKgco2eq` are NOT an uncertainty
 * interval and NOT a confidence interval. SIL-340 deliberately did not
 * invent uncertainty distributions for the grid-intensity factor or PUE —
 * only the power term carries a modeled band. On the measured path
 * (`boundsBasis = 'observed_p10_p90'`) the band is the p10/p90 of THIS
 * ASSET'S OBSERVED DAY-TO-DAY POWER DRAW over the observation window,
 * composed with the same (point) grid factor and PUE used for the central
 * estimate. On the archetype path (`boundsBasis = 'archetype_span'`) it is
 * Boavizta's own min/max for the archetype. Any label, tooltip, or doc
 * comment describing this field must say "observed power variation," never
 * "uncertainty interval" or "confidence interval." See
 * `USE_PHASE_BOUNDS_CAVEAT` below for the exact copy to reuse.
 * ---------------------------------------------------------------------------
 */

// ---------------------------------------------------------------------------
// Scope 2 labeling — CSRD/ESRS E1-6 requires the location/market-based
// qualifier be stated explicitly, everywhere. Never omit it.
// ---------------------------------------------------------------------------

/**
 * The ONLY string every Scope-2-labeled surface (asset detail, ESG CSV,
 * tooltip, methodology page) may render as the category label. Must say
 * "location-based" — CSRD/ESRS E1-6 expects both location- and market-based
 * Scope 2, and silently omitting the qualifier lets a customer drop this
 * number into a market-based field. We do not hold the customer's own
 * REC/GO contractual instruments, so a market-based figure cannot be
 * computed at all — see `USE_PHASE_NO_MARKET_BASED_STATEMENT`.
 */
export const USE_PHASE_SCOPE_LABEL = 'Scope 2 (location-based)';

/**
 * Companion statement — every surface that renders `USE_PHASE_SCOPE_LABEL`
 * must ALSO render (or link) this statement at least once. Silence on this
 * point is exactly the failure mode this constant exists to prevent.
 */
export const USE_PHASE_NO_MARKET_BASED_STATEMENT =
  'No market-based Scope 2 figure is provided. Market-based reporting requires the customer’s own REC/GO contractual instruments, which SiliconX does not hold.';

/**
 * Reusable copy for the bounds field — see the module-header "BOUNDS FIELD"
 * note. Every UI label/tooltip for `gwpUseAnnualMinKgco2eq`/`MaxKgco2eq`
 * should render this text (or a byte-for-byte subset of it), never invent
 * new wording that drifts toward "uncertainty"/"confidence interval."
 */
export const USE_PHASE_BOUNDS_CAVEAT =
  'This range reflects observed day-to-day variation in this asset’s power draw only. It is not a full uncertainty interval or a confidence interval — the grid-intensity factor and PUE carry no modeled uncertainty.';

/**
 * SIL-378 (Defect B) — the archetype-path sibling of `USE_PHASE_BOUNDS_CAVEAT`
 * above. `USE_PHASE_BOUNDS_CAVEAT` is TRUE only when `boundsBasis ===
 * 'observed_p10_p90'` (the range really is this asset's own observed
 * variation); on `boundsBasis === 'archetype_span'` there is ZERO
 * observation behind the range — it is BoaviztAPI's own min/max for the
 * generic archetype. Rendering `USE_PHASE_BOUNDS_CAVEAT` unconditionally on
 * that path is a false statement of fact (19 production assets across 7
 * orgs, at the time of SIL-378). Select between the two literals on
 * `boundsBasis` — never render both, never render the wrong one.
 */
export const USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE =
  'This range is BoaviztAPI’s own minimum-maximum for the generic server archetype used in place of measured power. It is not observed variation in this asset’s power draw, and it is not an uncertainty interval or a confidence interval — the grid-intensity factor and PUE carry no modeled uncertainty.';

/**
 * SIL-378 — the always-rendered screening-estimate line. Every COMPUTED
 * customer use-phase figure is a screening estimate — this must render
 * adjacent to the value/tier badge on every card, not buried only inside
 * the eligibility-specific reason string, which is the weakest surface for
 * it today.
 *
 * Deliberately does NOT assert "no asset reaches the highest accuracy
 * grade" — SIL-379 shipped an org-level PUE input, so `measured` is
 * reachable. That absolute claim is exactly the class of stale copy this
 * ticket exists to remove; do not reintroduce it here.
 */
export const USE_PHASE_SCREENING_ESTIMATE_LINE =
  'Screening estimate. Assess suitability for your own reporting before using this figure in an inventory.';

/**
 * /yr labels — deliberately named so a UI can never render the number
 * without the per-year qualifier in the same string. An annual rate is one
 * careless read from being booked as a cumulative lifetime total.
 */
export const USE_PHASE_ANNUAL_RATE_LABEL = 'Operational carbon (kgCO2e/yr)';
export const USE_PHASE_ANNUAL_RATE_MIN_LABEL = 'Operational carbon — low (kgCO2e/yr)';
export const USE_PHASE_ANNUAL_RATE_MAX_LABEL = 'Operational carbon — high (kgCO2e/yr)';

// ---------------------------------------------------------------------------
// Shared enums (zod mirrors of the SIL-341/SIL-340 TS unions)
// ---------------------------------------------------------------------------

export const usePhaseBoundsBasisSchema = z.enum(['observed_p10_p90', 'archetype_span']);
/** `satisfies`-style compile check that this mirrors `BoundsBasis` (use-phase-math.ts). */
type _BoundsBasisSchemaMatchesType = z.infer<typeof usePhaseBoundsBasisSchema> extends BoundsBasis
  ? BoundsBasis extends z.infer<typeof usePhaseBoundsBasisSchema>
    ? true
    : never
  : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _boundsBasisSchemaMatchesType: _BoundsBasisSchemaMatchesType = true;

export const usePhasePowerBasisSchema = z.enum(POWER_BASIS_VALUES);
export const usePhasePueSourceSchema = z.enum(PUE_SOURCE_VALUES);
export const usePhaseAccuracyTierSchema = z.enum(USE_PHASE_ACCURACY_TIER_VALUES);
export const usePhaseReportingEligibilitySchema = z.enum(
  USE_PHASE_REPORTING_ELIGIBILITY_VALUES,
);

/**
 * The subset of `USE_PHASE_REPORTING_ELIGIBILITY_VALUES` reachable from a
 * COMPUTED figure (i.e. everything except the two `not_computed_*` values).
 * Hardcoded rather than `.filter()`-derived so the zod enum keeps literal
 * tuple typing; the `satisfies`-style check below fails to compile if this
 * ever drifts from the source array in `accuracy-tier.ts`.
 */
export const COMPUTED_USE_PHASE_REPORTING_ELIGIBILITY_VALUES = [
  'reportable',
  'screening_estimate_default_location',
  'screening_estimate_default_pue',
  'screening_estimate_default_power',
  'screening_estimate_default_power_and_location',
] as const;
type ComputedUsePhaseReportingEligibility =
  (typeof COMPUTED_USE_PHASE_REPORTING_ELIGIBILITY_VALUES)[number];
type _ComputedEligibilityIsSubsetOfFull = ComputedUsePhaseReportingEligibility extends UsePhaseReportingEligibility
  ? true
  : never;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _computedEligibilityIsSubsetOfFull: _ComputedEligibilityIsSubsetOfFull = true;

const computedUsePhaseReportingEligibilitySchema = z.enum(
  COMPUTED_USE_PHASE_REPORTING_ELIGIBILITY_VALUES,
);

/**
 * Branded annual-rate figure (kgCO2e/yr) — following the `Uuid`/`Usd` brand
 * pattern in `dto/common.ts`. Distinct from `ProjectedUsePhaseKgCo2e`
 * below: an annual RATE and a scenario-horizon PROJECTION are never
 * interchangeable, and because these are two DIFFERENT brands (not one
 * brand vs. plain `number`), TypeScript's structural widening rule that
 * lets a branded value satisfy an unbranded `number` slot does NOT apply
 * here — assigning a `ProjectedUsePhaseKgCo2e` into a field typed
 * `UsePhaseAnnualRateKgCo2eqPerYr` is a genuine, uncastable-without-a-cast
 * compile error (two incompatible nominal brands), which is the mechanism
 * deliverable 3 actually needs. See the `@ts-expect-error` tests in
 * `__tests__/use-phase-dto.test.ts`.
 */
export const usePhaseAnnualRateKgco2eqPerYrSchema = z
  .coerce.number()
  .brand('UsePhaseAnnualRateKgCo2eqPerYr');
export type UsePhaseAnnualRateKgCo2eqPerYr = z.infer<
  typeof usePhaseAnnualRateKgco2eqPerYrSchema
>;

// ---------------------------------------------------------------------------
// Deliverable 2 — pending-first-computation vs unsupported-asset-class,
// as a type-system-level discriminant (not just a string).
// ---------------------------------------------------------------------------

/**
 * Why a use-phase figure has NOT been computed for this asset. Precedent:
 * the embodied DTO's `pending_classification` (`dto/carbon.ts`).
 *
 *   - `pending_first_computation` — the asset is real and supported, but
 *     has no snapshot yet (newly onboarded; waits for the next monthly
 *     pass). This is NOT "not applicable" — treating it as such reads as
 *     broken software, not as "come back next month."
 *   - `unsupported_asset_class` — this asset class never gets a use-phase
 *     figure (e.g. it cannot draw power that is ours to report — `memory`,
 *     `cloud_vm`; see `asset-class-support.ts`). This one IS "not
 *     applicable," permanently.
 *   - `no_archetype_without_telemetry` (SIL-368) — the asset class DOES
 *     draw power and IS use-phase-supported, but has no server-chassis
 *     identity for BoaviztAPI to model an archetype default from (a switch
 *     or a bare GPU is not a server) — so, unlike `'server'`, it can ONLY
 *     be computed once real telemetry exists. This is NOT "not
 *     applicable" (the asset will get a figure once telemetry arrives) and
 *     it is NOT the generic "hasn't run yet" of `pending_first_computation`
 *     (a `'server'`-class asset in that state gets an archetype-default
 *     figure on the very next monthly pass regardless of telemetry; this
 *     class never will, until telemetry shows up). Distinguishing the two
 *     matters because conflating them into a single "pending" bucket masks
 *     the difference between "wait a month" and "wait for someone to wire
 *     up telemetry."
 */
export const usePhaseNotComputedReasonSchema = z.enum([
  'pending_first_computation',
  'unsupported_asset_class',
  'no_archetype_without_telemetry',
]);
export type UsePhaseNotComputedReason = z.infer<typeof usePhaseNotComputedReasonSchema>;

/**
 * Resolves which `UsePhaseNotComputedReason` applies for an asset with NO
 * `asset_use_phase_snapshot` row yet (SIL-368). `hasTelemetry` is an
 * EXISTENCE check only — "has this asset ever recorded a telemetry_daily_agg
 * row," not a re-derivation of the ingestion CLI's own period-scoped
 * selection/qualification logic (`compute-use-phase.ts`'s
 * `resolvePower`/`isMeasuredPowerQualified`). Callers (the ops/customer
 * asset-detail routes, the ESG CSV export) already have or can cheaply add
 * this existence check; this function is the SINGLE place that turns
 * (asset class, has-telemetry) into the right reason, so the three-way
 * choice can never drift between surfaces.
 *
 * @pure
 */
export function resolveUsePhaseNotComputedReason(
  assetClassId: string,
  hasTelemetry: boolean,
): UsePhaseNotComputedReason {
  if (!isUsePhaseSupportedAssetClass(assetClassId)) return 'unsupported_asset_class';
  if (isUsePhaseMeasuredOnlyAssetClass(assetClassId) && !hasTelemetry) {
    return 'no_archetype_without_telemetry';
  }
  return 'pending_first_computation';
}

const usePhaseComputedFigureSchema = z.object({
  status: z.literal('computed'),

  // Uncertainty bundle — see module-header BOUNDS FIELD note. NEVER label
  // these as an uncertainty/confidence interval. Branded
  // UsePhaseAnnualRateKgCo2eqPerYr, distinct from ProjectedUsePhaseKgCo2e —
  // see that brand's docstring.
  gwpUseAnnualKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  gwpUseAnnualMinKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  gwpUseAnnualMaxKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  boundsBasis: usePhaseBoundsBasisSchema,

  // Resolved inputs.
  usageLocation: z.string().length(3),
  gridIntensityGPerKwh: z.coerce.number(),
  /** Resolved PUE actually applied on Scope-2-labeled surfaces via `applyPue()`. */
  pue: z.coerce.number(),
  pueSource: usePhasePueSourceSchema,
  /** Device-level watts (PUE NOT applied to this figure). */
  resolvedWatts: z.coerce.number(),
  powerBasis: usePhasePowerBasisSchema,

  // Observation window.
  observationStart: z.string().nullable(),
  observationEnd: z.string().nullable(),

  periodKey: z.string(),
  methodologyVersion: z.string(),

  // Derived, every read, from input_provenance — never stored, never trusted
  // from a prior response. See SIL-341 (accuracy-tier.ts).
  accuracyTier: usePhaseAccuracyTierSchema,
  reportingEligibility: computedUsePhaseReportingEligibilitySchema,
});
export type UsePhaseComputedFigure = z.infer<typeof usePhaseComputedFigureSchema>;

const usePhaseNotComputedFigureSchema = z.object({
  status: z.literal('not_computed'),
  reason: usePhaseNotComputedReasonSchema,
  /** Mechanically derived from `reason` by `buildNotComputedUsePhaseFigure` —
   * never set independently, so the two can't drift apart. */
  reportingEligibility: z.enum([
    'not_computed_pending_first_computation',
    'not_computed_unsupported_asset_class',
    'not_computed_no_archetype_without_telemetry',
  ]),
});
export type UsePhaseNotComputedFigure = z.infer<typeof usePhaseNotComputedFigureSchema>;

/**
 * The read DTO for a use-phase figure (deliverable 1). A discriminated
 * union on `status` — `'computed'` vs `'not_computed'` — so TypeScript
 * itself, not a string comparison, forces every consumer to handle the
 * no-snapshot-yet case distinctly from the has-a-snapshot case. Within
 * `'not_computed'`, `reason` further discriminates
 * `pending_first_computation` from `unsupported_asset_class` (deliverable
 * 2) — a `switch` over `reason` that omits a case is a `never` type error,
 * not a silent identical render.
 */
export const usePhaseFigureSchema = z.discriminatedUnion('status', [
  usePhaseComputedFigureSchema,
  usePhaseNotComputedFigureSchema,
]);
export type UsePhaseFigure = z.infer<typeof usePhaseFigureSchema>;

// ---------------------------------------------------------------------------
// DTO builders
// ---------------------------------------------------------------------------

/**
 * The subset of `asset_use_phase_snapshot` columns (707) needed to build a
 * computed figure. Deliberately camelCase / narrow — callers (apps/api) map
 * their Kysely row shape into this, so this module never assumes a
 * particular DB client's casing.
 */
export interface UsePhaseSnapshotRow {
  gwpUseAnnualKgco2eq: number;
  gwpUseAnnualMinKgco2eq: number;
  gwpUseAnnualMaxKgco2eq: number;
  boundsBasis: BoundsBasis;
  usageLocation: string;
  gridIntensityGPerKwh: number;
  resolvedWatts: number;
  powerBasis: UsePhasePowerBasis;
  observationStart: string | null;
  observationEnd: string | null;
  periodKey: string;
  methodologyVersion: string;
}

/**
 * Builds the `'computed'` branch of `UsePhaseFigure` from a snapshot row
 * plus its (unparsed) `input_provenance` jsonb blob. Parses the provenance
 * with `usePhaseInputProvenanceSchema` and calls SIL-341's
 * `deriveUsePhaseAccuracyTier` / `deriveReportingEligibility` directly —
 * this is the ONLY place in this module that computes a tier, and it is
 * recomputed on every call. Never cache the result across requests.
 *
 * Throws (via zod) if `rawInputProvenance` doesn't parse — a malformed
 * `input_provenance` blob is a data-integrity bug worth surfacing loudly,
 * not silently degrading to a placeholder tier.
 */
export function buildComputedUsePhaseFigure(
  row: UsePhaseSnapshotRow,
  rawInputProvenance: unknown,
): UsePhaseComputedFigure {
  const provenance = usePhaseInputProvenanceSchema.parse(rawInputProvenance);
  const accuracyTier: UsePhaseAccuracyTier = deriveUsePhaseAccuracyTier(provenance);
  const reportingEligibility = deriveReportingEligibility(provenance);

  return usePhaseComputedFigureSchema.parse({
    status: 'computed',
    gwpUseAnnualKgco2eq: row.gwpUseAnnualKgco2eq,
    gwpUseAnnualMinKgco2eq: row.gwpUseAnnualMinKgco2eq,
    gwpUseAnnualMaxKgco2eq: row.gwpUseAnnualMaxKgco2eq,
    boundsBasis: row.boundsBasis,
    usageLocation: row.usageLocation,
    gridIntensityGPerKwh: row.gridIntensityGPerKwh,
    pue: provenance.pue.value,
    pueSource: provenance.pue.source,
    resolvedWatts: row.resolvedWatts,
    powerBasis: row.powerBasis,
    observationStart: row.observationStart,
    observationEnd: row.observationEnd,
    periodKey: row.periodKey,
    methodologyVersion: row.methodologyVersion,
    accuracyTier,
    reportingEligibility,
  });
}

/**
 * Builds the `'not_computed'` branch. `reportingEligibility` is derived
 * MECHANICALLY from `reason` (not passed independently) so the two enums
 * can never disagree — the eligibility enum's `not_computed_*` values exist
 * for exactly one reason each.
 */
export function buildNotComputedUsePhaseFigure(
  reason: UsePhaseNotComputedReason,
): UsePhaseNotComputedFigure {
  const reportingEligibility: UsePhaseNotComputedFigure['reportingEligibility'] =
    reason === 'pending_first_computation'
      ? 'not_computed_pending_first_computation'
      : reason === 'no_archetype_without_telemetry'
        ? 'not_computed_no_archetype_without_telemetry'
        : 'not_computed_unsupported_asset_class';

  return usePhaseNotComputedFigureSchema.parse({
    status: 'not_computed',
    reason,
    reportingEligibility,
  });
}

// ---------------------------------------------------------------------------
// Deliverable 3 — branded ProjectedUsePhaseKgCo2e, following the existing
// `Uuid` brand in dto/common.ts (dto/financials.ts consumes that same
// pattern for its own branded fields).
// ---------------------------------------------------------------------------

/**
 * A scenario/what-if projected use-phase figure (kgCO2e over some horizon —
 * NOT an annual rate; scenario callers own their own horizon math and pass
 * the already-computed total in). Branded so it is structurally distinct
 * from every plain `number` and from the annual-rate fields on
 * `UsePhaseComputedFigure` above.
 *
 * The point is mechanical enforcement, not documentation: no export/CSV
 * serializer in this package accepts `ProjectedUsePhaseKgCo2e` (see
 * `UsePhaseCsvExportFields` below and the `@ts-expect-error` test in
 * `__tests__/use-phase-dto.test.ts`). Routing a what-if projection into the
 * ESG export requires an explicit, named unbrand cast
 * (`projectedUsePhaseKgco2eqSchema.parse(x as number)` or similar) that
 * shows up in `grep` and in code review. A scenario forecast and an
 * inventory line must never share an output path.
 */
export const projectedUsePhaseKgco2eqSchema = z.coerce.number().brand('ProjectedUsePhaseKgCo2e');
export type ProjectedUsePhaseKgCo2e = z.infer<typeof projectedUsePhaseKgco2eqSchema>;

/**
 * Projects an annual use-phase rate over a scenario horizon. The INPUT is
 * the branded `UsePhaseAnnualRateKgCo2eqPerYr` (deliverable 5's `...PerYr`
 * naming, enforced as a real type here rather than just a naming
 * convention); the OUTPUT is the DIFFERENTLY-branded
 * `ProjectedUsePhaseKgCo2e`, deliberately not reusing the rate brand,
 * because it is a cumulative total over `horizonMonths`, not a rate. A
 * caller with only a raw `number` must first validate it with
 * `usePhaseAnnualRateKgco2eqPerYrSchema.parse(...)` — same discipline as
 * every other branded field in this package.
 *
 * @pure
 */
export function projectUsePhaseKgco2e(
  annualRateKgco2eqPerYr: UsePhaseAnnualRateKgCo2eqPerYr,
  horizonMonths: number,
): ProjectedUsePhaseKgCo2e {
  const years = horizonMonths / 12;
  return projectedUsePhaseKgco2eqSchema.parse(
    (annualRateKgco2eqPerYr as number) * years,
  );
}

// ---------------------------------------------------------------------------
// ESG CSV export fields (plan §"ESG CSV export" appended-columns list).
// Deliberately unbranded plain numbers — this is the type the
// `@ts-expect-error` test asserts `ProjectedUsePhaseKgCo2e` cannot satisfy
// without a cast.
// ---------------------------------------------------------------------------

export const usePhaseCsvExportFieldsSchema = z.object({
  gwpUseAnnualKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema.nullable(),
  gwpUseAnnualMinKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema.nullable(),
  gwpUseAnnualMaxKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema.nullable(),
  /** Always `USE_PHASE_SCOPE_LABEL` — a separate structural column from the
   * frozen `ghg_protocol_category` column, which scopes to embodied only. */
  usePhaseGhgCategory: z.literal(USE_PHASE_SCOPE_LABEL),
  usePhaseBoundsBasis: usePhaseBoundsBasisSchema.nullable(),
  usageLocation: z.string().length(3).nullable(),
  gridIntensityGPerKwh: z.coerce.number().nullable(),
  pue: z.coerce.number().nullable(),
  pueSource: usePhasePueSourceSchema.nullable(),
  observationStart: z.string().nullable(),
  observationEnd: z.string().nullable(),
  /** Full enum, including both `not_computed_*` values — nothing is
   * withheld from the export (plan decision 13). */
  usePhaseReportingEligibility: usePhaseReportingEligibilitySchema,
  usePhaseComputedAt: z.string().nullable(),
});
export type UsePhaseCsvExportFields = z.infer<typeof usePhaseCsvExportFieldsSchema>;

/**
 * Builds one CSV row's worth of use-phase columns from a `UsePhaseFigure`.
 * Preserves the "never drop assets" invariant (`carbon-export.ts`): a
 * `not_computed` figure yields blank figure columns plus the appropriate
 * `not_computed_*` eligibility value, never a dropped row.
 *
 * Takes `UsePhaseFigure` (unbranded numbers throughout) — a caller cannot
 * pass a `ProjectedUsePhaseKgCo2e` here without first unbranding it, because
 * nothing on this function's parameter type is that brand.
 */
export function buildUsePhaseCsvExportFields(figure: UsePhaseFigure): UsePhaseCsvExportFields {
  if (figure.status === 'not_computed') {
    return usePhaseCsvExportFieldsSchema.parse({
      gwpUseAnnualKgco2eq: null,
      gwpUseAnnualMinKgco2eq: null,
      gwpUseAnnualMaxKgco2eq: null,
      usePhaseGhgCategory: USE_PHASE_SCOPE_LABEL,
      usePhaseBoundsBasis: null,
      usageLocation: null,
      gridIntensityGPerKwh: null,
      pue: null,
      pueSource: null,
      observationStart: null,
      observationEnd: null,
      usePhaseReportingEligibility: figure.reportingEligibility,
      usePhaseComputedAt: null,
    });
  }

  return usePhaseCsvExportFieldsSchema.parse({
    gwpUseAnnualKgco2eq: figure.gwpUseAnnualKgco2eq,
    gwpUseAnnualMinKgco2eq: figure.gwpUseAnnualMinKgco2eq,
    gwpUseAnnualMaxKgco2eq: figure.gwpUseAnnualMaxKgco2eq,
    usePhaseGhgCategory: USE_PHASE_SCOPE_LABEL,
    usePhaseBoundsBasis: figure.boundsBasis,
    usageLocation: figure.usageLocation,
    gridIntensityGPerKwh: figure.gridIntensityGPerKwh,
    pue: figure.pue,
    pueSource: figure.pueSource,
    observationStart: figure.observationStart,
    observationEnd: figure.observationEnd,
    usePhaseReportingEligibility: figure.reportingEligibility,
    usePhaseComputedAt: null, // populated by the caller from the snapshot row's computed_at
  });
}

// ---------------------------------------------------------------------------
// Deliverable 4 — avoided-emissions and inventory figures in SEPARATE
// top-level objects. No helper function anywhere in this module (or
// dto/carbon.ts) accepts both.
// ---------------------------------------------------------------------------

/**
 * Combined per-asset carbon summary, bundling the EXISTING embodied
 * "avoided" claim (`dto/carbon.ts`'s `listingCarbonClaimFieldsSchema`)
 * alongside the NEW use-phase (operational) inventory figure.
 *
 * GHG Protocol Scope 3 Technical Guidance forbids netting these, in three
 * places:
 *   "Any claims of avoided emissions related to a company's sold products
 *    must be reported separately from the company's scope 1, scope 2, and
 *    scope 3 inventories." (p114)
 *   "Any claims of avoided emissions... should not be included in, or
 *    deducted from, the scope 3 inventory, but may instead be reported
 *    separately." (p79)
 *
 * Enforced STRUCTURALLY, not just by review convention:
 *   - `avoidedEmissions` and `useEmissions` are separate top-level keys on
 *     this object — there is no field that combines or nets them.
 *   - There is no exported function in this module, or in `dto/carbon.ts`,
 *     whose signature accepts BOTH an avoided-emissions figure (e.g.
 *     `ListingCarbonClaimFields`) and an inventory figure (`UsePhaseFigure`
 *     or `UsePhaseCsvExportFields`). If a future change adds one, it
 *     violates this invariant — see the "no combined helper" test in
 *     `__tests__/use-phase-dto.test.ts`, which documents the check and
 *     should be extended if this module's export surface grows.
 */
export const assetCarbonSummarySchema = z.object({
  /** Embodied "up to X kg CO2eq avoided" claim — SIL-191/193, unchanged. */
  avoidedEmissions: listingCarbonClaimFieldsSchema,
  /** Use-phase (Scope 2, location-based) operational inventory figure. */
  useEmissions: usePhaseFigureSchema,
});
export type AssetCarbonSummary = z.infer<typeof assetCarbonSummarySchema>;

// ---------------------------------------------------------------------------
// SIL-344 (T6) — ops + customer asset-detail read DTOs.
//
// Both wrap `UsePhaseFigure` (built by `buildComputedUsePhaseFigure` /
// `buildNotComputedUsePhaseFigure` above from a `v_asset_use_phase_latest`
// row) for their respective surface, per the plan's §Surfaces table:
//   - ops    -> diagnostic: device-level rate + bounds + bounds_basis +
//               derived tier/eligibility + the FULL parsed input_provenance
//               (resolved_watts / grid_intensity_g_per_kwh must stay
//               visible — that's what makes a wrong figure diagnosable).
//   - customer -> Scope 2 (location-based): the SAME figure with PUE
//               APPLIED via the one shared `applyPue()` — a device-level
//               number under a Scope 2 label is systematically ~1.3-1.6x
//               low (use-phase-math.ts's own docstring). Never the ops
//               response's raw `figure.gwpUseAnnual*` on this surface.
// ---------------------------------------------------------------------------

/**
 * Narrow, TOLERANT parse of `asset_use_phase_snapshot.input_payload` — SIL-380.
 * `input_payload` is the exact payload sent to BoaviztAPI (see
 * `apps/ingestion/src/pipeline/carbon-use-phase.ts`'s `inputPayload` object);
 * it is NOT validated by `usePhaseInputProvenanceSchema` above (that schema
 * covers the separate `input_provenance` audit column). This schema pulls
 * out exactly the four fields the ops reconciliation derivation block needs
 * to reproduce a figure — `useTimeRatio` above all, since that value lives
 * ONLY in `input_payload` and nowhere else on the snapshot row.
 *
 * ALL FOUR FIELDS ARE NULLABLE/OPTIONAL, deliberately: an older row, or a
 * row whose payload shape drifts in a future ingestion change, must degrade
 * to a visible "field absent" state (never a NaN in the UI's arithmetic).
 * `parseUsePhaseInputPayloadDiagnostic` below is the ONLY entry point —
 * never `.parse()` this schema directly against a raw jsonb blob without
 * going through the tolerant wrapper.
 */
// Each field: accept absent/undefined (older row / drifted payload shape)
// OR null OR a coercible number, but always NORMALIZE the output to
// `number | null` (never `undefined`) via `.transform` — this keeps the
// zod-inferred type identical to the hand-written
// `UsePhaseInputPayloadDiagnostic` below, with no optional/undefined member,
// so every consumer can treat a missing field exactly like an explicit null.
const tolerantNullableNumber = z.coerce
  .number()
  .nullable()
  .optional()
  .transform((v) => v ?? null);

export const usePhaseInputPayloadDiagnosticSchema = z.object({
  useTimeRatio: tolerantNullableNumber,
  methodologyDurationHours: tolerantNullableNumber,
  resolvedWatts: tolerantNullableNumber,
  gridIntensityGPerKwh: tolerantNullableNumber,
});
export type UsePhaseInputPayloadDiagnostic = z.infer<typeof usePhaseInputPayloadDiagnosticSchema>;

/**
 * Tolerantly parses a raw `input_payload` jsonb blob into
 * `UsePhaseInputPayloadDiagnostic`. Never throws — a payload that isn't even
 * an object, or is missing every field, degrades to all-`null` rather than
 * surfacing a parse error to the ops panel; a missing/malformed
 * `input_payload` is diagnosable data on its own, not a reason to break the
 * page.
 */
export function parseUsePhaseInputPayloadDiagnostic(raw: unknown): UsePhaseInputPayloadDiagnostic {
  const result = usePhaseInputPayloadDiagnosticSchema.safeParse(raw);
  if (!result.success) {
    return {
      useTimeRatio: null,
      methodologyDurationHours: null,
      resolvedWatts: null,
      gridIntensityGPerKwh: null,
    };
  }
  return {
    useTimeRatio: result.data.useTimeRatio ?? null,
    methodologyDurationHours: result.data.methodologyDurationHours ?? null,
    resolvedWatts: result.data.resolvedWatts ?? null,
    gridIntensityGPerKwh: result.data.gridIntensityGPerKwh ?? null,
  };
}

/**
 * Ops asset-detail response. `inputProvenance` is the parsed, AUTHORITATIVE
 * audit record `accuracyTier`/`reportingEligibility` on `figure` were
 * derived from (SIL-341) — present whenever `figure.status === 'computed'`,
 * `null` otherwise (no snapshot row to have provenance from).
 *
 * SIL-380 additions, all present only alongside a `'computed'` figure:
 *   - `inputPayload` — the tolerant four-field parse of the exact BoaviztAPI
 *     request payload (`useTimeRatio` above all — needed to reproduce
 *     `figure.gwpUseAnnualKgco2eq` by hand).
 *   - `rawResponse` — BoaviztAPI's raw response, passed through UNTYPED
 *     (`z.unknown()`) on purpose: it is a third-party shape that changes
 *     across BoaviztAPI releases, and a typed schema here is a promise this
 *     codebase would eventually break. Render it as
 *     `JSON.stringify(value, null, 2)` inside an expander, never modeled.
 *   - `screeningBasis` — every defaulted input term behind this figure (see
 *     `use-phase/screening-basis.ts`), not just the tie-broken
 *     `reportingEligibility` scalar already on `figure`.
 */
export const usePhaseOpsDetailResponseSchema = z.object({
  figure: usePhaseFigureSchema,
  inputProvenance: usePhaseInputProvenanceSchema.nullable(),
  inputPayload: usePhaseInputPayloadDiagnosticSchema.nullable().default(null),
  rawResponse: z.unknown().nullable().default(null),
  screeningBasis: usePhaseScreeningBasisSchema.nullable().default(null),
});
export type UsePhaseOpsDetailResponse = z.infer<typeof usePhaseOpsDetailResponseSchema>;

/**
 * Builds the ops diagnostic response. `figure` already carries
 * `resolvedWatts` / `gridIntensityGPerKwh` (device-level, no PUE) plus the
 * derived tier/eligibility; this bundles the full parsed provenance
 * alongside it so an ops analyst can see WHY a tier landed where it did, not
 * just that it did — plus (SIL-380) the raw diagnostic material needed to
 * REPRODUCE the figure: the tolerantly-parsed `input_payload` four-field
 * subset, the passthrough raw BoaviztAPI response, and the derived
 * `screeningBasis`.
 *
 * `raw` is `null` for a `'not_computed'` figure (no snapshot row exists to
 * have an `input_payload`/`raw_response` from) and MAY be omitted by older
 * call sites — it defaults to `null`, so `inputPayload`/`rawResponse`/
 * `screeningBasis` all come back `null` rather than the call failing.
 */
export function buildUsePhaseOpsDetailResponse(
  figure: UsePhaseFigure,
  inputProvenance: UsePhaseInputProvenance | null,
  raw: { inputPayload: unknown; rawResponse: unknown } | null = null,
): UsePhaseOpsDetailResponse {
  const inputPayload = raw ? parseUsePhaseInputPayloadDiagnostic(raw.inputPayload) : null;
  const screeningBasis =
    figure.status === 'computed' && inputProvenance
      ? deriveUsePhaseScreeningBasis(inputProvenance, figure.boundsBasis)
      : null;

  return usePhaseOpsDetailResponseSchema.parse({
    figure,
    inputProvenance,
    inputPayload,
    rawResponse: raw ? raw.rawResponse : null,
    screeningBasis,
  });
}

// ---------------------------------------------------------------------------
// SIL-348 (T10) — ops asset-detail snapshot history. `asset_use_phase_
// snapshot` is append-only, so a step change (methodology bump, telemetry
// arriving mid-series) is only visible across MULTIPLE rows — the single-row
// "latest" response above cannot show it. Deliberately thin per row (no
// input_provenance, no bounds) — this is a trend list, not another
// diagnostic payload; the "why" for any one row is the detail response above.
// ---------------------------------------------------------------------------

export const usePhaseSnapshotHistoryEntrySchema = z.object({
  periodKey: z.string(),
  computedAt: z.string(),
  methodologyVersion: z.string(),
  gwpUseAnnualKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  boundsBasis: usePhaseBoundsBasisSchema,
  powerBasis: usePhasePowerBasisSchema,
});
export type UsePhaseSnapshotHistoryEntry = z.infer<typeof usePhaseSnapshotHistoryEntrySchema>;

export const usePhaseHistoryResponseSchema = z.object({
  data: z.array(usePhaseSnapshotHistoryEntrySchema),
});
export type UsePhaseHistoryResponse = z.infer<typeof usePhaseHistoryResponseSchema>;

/**
 * Customer-facing computed figure — SAME semantics as
 * `UsePhaseComputedFigure` except `gwpUseAnnual{,Min,Max}Kgco2eq` are
 * PUE-APPLIED (Scope 2 purchased-electricity figures, not device-level).
 * Deliberately drops `resolvedWatts` — the diagnostic device-level wattage
 * is an ops-only field; the customer surface's job is the Scope 2 number,
 * not the raw device draw it was derived from.
 *
 * SIL-378 additions — `powerBasis`, `daysWithData`, `calendarDaysInWindow`.
 * NOT the same fields as `UsePhaseComputedFigure` (the OPS figure above,
 * which already carries `powerBasis` from the snapshot row directly): here
 * `daysWithData`/`calendarDaysInWindow` come from the parsed
 * `input_provenance.power` and exist so the customer card can distinguish
 * "no telemetry at all" (`powerBasis === 'archetype_default'`) from
 * "telemetry exists but failed the measured-power coverage/window gate"
 * (`powerBasis === 'telemetry_measured'` with `deriveReportingEligibility`
 * still mapping to a screening-estimate value) and can quote the actual
 * coverage numbers rather than a generic "no telemetry" claim (Defect A).
 */
const usePhaseCustomerComputedFigureSchema = z.object({
  status: z.literal('computed'),
  gwpUseAnnualKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  gwpUseAnnualMinKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  gwpUseAnnualMaxKgco2eq: usePhaseAnnualRateKgco2eqPerYrSchema,
  boundsBasis: usePhaseBoundsBasisSchema,
  usageLocation: z.string().length(3),
  pue: z.coerce.number(),
  pueSource: usePhasePueSourceSchema,
  observationStart: z.string().nullable(),
  observationEnd: z.string().nullable(),
  periodKey: z.string(),
  accuracyTier: usePhaseAccuracyTierSchema,
  reportingEligibility: computedUsePhaseReportingEligibilitySchema,
  powerBasis: usePhasePowerBasisSchema,
  /** Count of calendar days in the observation window with >=1 telemetry
   * row — from `input_provenance.power.daysWithData`. */
  daysWithData: z.number().int().nonnegative(),
  /** Calendar days in the observation window (the coverage denominator) —
   * from `input_provenance.power.calendarDaysInWindow`. */
  calendarDaysInWindow: z.number().int().nonnegative(),
  /**
   * SIL-388/389 — plain-language accelerator-power disclosure, `null`
   * unless `input_provenance.gpu` is populated (i.e. accelerator power was
   * actually folded into this figure). Deliberately names BOTH assumptions
   * (population = slot capacity, an upper bound; workload = 50% of TDP) in
   * one customer-safe sentence, built by `buildAcceleratorAssumptionCopy`
   * — never re-authored per-surface, same discipline as `boundsCaveat` /
   * `screeningEstimateLine` above.
   */
  acceleratorAssumption: z.string().nullable().default(null),
});
export type UsePhaseCustomerComputedFigure = z.infer<typeof usePhaseCustomerComputedFigureSchema>;

/**
 * SIL-388/389 — builds the customer-safe accelerator-assumption sentence
 * from a parsed `input_provenance.gpu` block. `null` in, `null` out (no
 * accelerator power was modeled for this figure — either not a GPU asset,
 * measured power already includes GPU draw, or the model has no TDP-table
 * entry). Kept as one function so ops/customer copy can never drift from
 * each other on the underlying facts, even though the two audiences get
 * different detail levels (this one is lay-reader; the ops panel renders
 * the full provenance block directly).
 *
 * @pure
 */
export function buildAcceleratorAssumptionCopy(
  gpu: UsePhaseInputProvenance['gpu'],
): string | null {
  if (!gpu) return null;
  const workloadPct = Math.round(gpu.workloadDerateFactor * 100);
  return (
    `Includes an assumed accelerator draw: ${gpu.units}× ${gpu.name} at ${gpu.avgPowerPerUnitWatts}W each ` +
    `(${gpu.tdpWatts}W manufacturer TDP × ${workloadPct}% modeled workload). The accelerator count is this ` +
    `hardware's SLOT CAPACITY, not a confirmed installed count — this figure assumes every slot is populated, ` +
    `which is an upper bound for a partially-populated chassis.`
  );
}

export const usePhaseCustomerFigureSchema = z.discriminatedUnion('status', [
  usePhaseCustomerComputedFigureSchema,
  usePhaseNotComputedFigureSchema,
]);
export type UsePhaseCustomerFigure = z.infer<typeof usePhaseCustomerFigureSchema>;

/**
 * Customer asset-detail response. Always carries the Scope 2 label + the
 * no-market-based-figure statement + the bounds caveat + the
 * /yr-unmissable annual-rate label + (SIL-378) the always-rendered
 * screening-estimate line, so no consuming UI can render the figure
 * without also rendering the required qualifiers (plan §Surfaces /
 * §"PUE — applied, not footnoted" / §Bounds).
 *
 * SIL-378 (Defect B) widens `boundsCaveat` to a union of
 * `USE_PHASE_BOUNDS_CAVEAT` (the `observed_p10_p90` path — unchanged,
 * still rendered verbatim) and `USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE` (the
 * `archetype_span` path, where the range carries zero observation).
 * Additive — a union of two literals is not a contract break for any
 * existing consumer already switching on exact string equality with
 * `USE_PHASE_BOUNDS_CAVEAT`.
 */
export const usePhaseCustomerDetailResponseSchema = z.object({
  figure: usePhaseCustomerFigureSchema,
  scopeLabel: z.literal(USE_PHASE_SCOPE_LABEL),
  noMarketBasedStatement: z.literal(USE_PHASE_NO_MARKET_BASED_STATEMENT),
  boundsCaveat: z.union([
    z.literal(USE_PHASE_BOUNDS_CAVEAT),
    z.literal(USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE),
  ]),
  annualRateLabel: z.literal(USE_PHASE_ANNUAL_RATE_LABEL),
  screeningEstimateLine: z.literal(USE_PHASE_SCREENING_ESTIMATE_LINE),
});
export type UsePhaseCustomerDetailResponse = z.infer<typeof usePhaseCustomerDetailResponseSchema>;

/**
 * Builds the customer-facing (Scope 2, location-based) response from the
 * device-level `UsePhaseFigure`. Applies PUE via the ONE shared `applyPue()`
 * for every rate field — this is the ONLY place in the ops/customer route
 * layer that is allowed to call `applyPue`; routes must never re-implement
 * the multiplication inline. `not_computed` passes through unchanged
 * (nothing to apply PUE to).
 *
 * `inputProvenance` (SIL-378) is the SAME parsed provenance the caller
 * already has from `buildComputedUsePhaseFigure` — required whenever
 * `figure.status === 'computed'` (there is no `daysWithData`/
 * `calendarDaysInWindow` anywhere else to source them from), `null`
 * otherwise (a `not_computed` figure has no snapshot row, hence no
 * provenance).
 */
export function buildUsePhaseCustomerDetailResponse(
  figure: UsePhaseFigure,
  inputProvenance: UsePhaseInputProvenance | null = null,
): UsePhaseCustomerDetailResponse {
  const customerFigure: UsePhaseCustomerFigure =
    figure.status === 'not_computed'
      ? figure
      : usePhaseCustomerComputedFigureSchema.parse({
          status: 'computed',
          gwpUseAnnualKgco2eq: applyPue(figure.gwpUseAnnualKgco2eq, figure.pue),
          gwpUseAnnualMinKgco2eq: applyPue(figure.gwpUseAnnualMinKgco2eq, figure.pue),
          gwpUseAnnualMaxKgco2eq: applyPue(figure.gwpUseAnnualMaxKgco2eq, figure.pue),
          boundsBasis: figure.boundsBasis,
          usageLocation: figure.usageLocation,
          pue: figure.pue,
          pueSource: figure.pueSource,
          observationStart: figure.observationStart,
          observationEnd: figure.observationEnd,
          periodKey: figure.periodKey,
          accuracyTier: figure.accuracyTier,
          reportingEligibility: figure.reportingEligibility,
          powerBasis: figure.powerBasis,
          daysWithData: inputProvenance?.power.daysWithData ?? 0,
          calendarDaysInWindow: inputProvenance?.power.calendarDaysInWindow ?? 0,
          acceleratorAssumption: buildAcceleratorAssumptionCopy(inputProvenance?.gpu ?? null),
        });

  const boundsCaveat =
    figure.status === 'computed' && figure.boundsBasis === 'archetype_span'
      ? USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE
      : USE_PHASE_BOUNDS_CAVEAT;

  return usePhaseCustomerDetailResponseSchema.parse({
    figure: customerFigure,
    scopeLabel: USE_PHASE_SCOPE_LABEL,
    noMarketBasedStatement: USE_PHASE_NO_MARKET_BASED_STATEMENT,
    boundsCaveat,
    annualRateLabel: USE_PHASE_ANNUAL_RATE_LABEL,
    screeningEstimateLine: USE_PHASE_SCREENING_ESTIMATE_LINE,
  });
}
